<template>
  <base-nav
    v-model="showMenu"
    class="navbar-absolute top-navbar"
    type="white"
    :transparent="true"
  >
    <div slot="brand" class="navbar-wrapper">
      <div class="navbar-minimize d-inline"><sidebar-toggle-button /></div>
      <div
        class="navbar-toggle d-inline"
        :class="{ toggled: $sidebar.showSidebar }"
      >
        <button type="button" class="navbar-toggler" @click="toggleSidebar">
          <span class="navbar-toggler-bar bar1"></span>
          <span class="navbar-toggler-bar bar2"></span>
          <span class="navbar-toggler-bar bar3"></span>
        </button>
      </div>
      <a class="navbar-brand" href="#pablo">{{ routeName }}</a>
    </div>

    <ul class="navbar-nav" :class="$rtl.isRTL ? 'mr-auto' : 'ml-auto'" style="align-items: center;">
      <!-- Search Button (Admin Only) -->
      <div class="search-bar input-group" @click="searchModalVisible = true" v-if="isAdmin">
        <button
          class="btn btn-link"
          id="search-button"
          data-toggle="modal"
          data-target="#searchModal"
        >
          <i class="tim-icons icon-zoom-split"></i>
        </button>
      </div>
      
      <!-- Search Modal -->
      <modal
        :show.sync="searchModalVisible"
        class="modal-search"
        id="searchModal"
        :centered="false"
        :show-close="true"
        v-if="isAdmin"
      >
        <form @submit.prevent="performSearch" slot="header">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            id="inlineFormInputGroup"
            :placeholder="$t('Search') + '...'"
            autofocus
          />
        </form>
      </modal>
      
      <!-- Campaign Sending Progress (only visible when active) -->
      <li class="nav-item campaign-progress-item" v-if="campaignJob && campaignJob.active">
        <div class="campaign-progress">
          <i
            class="tim-icons icon-send"
            :class="{ 'sending-animation': !isCampaignQuotaWaiting(campaignJob.job) }"
          ></i>
          <span class="progress-text">
            {{ campaignJob.job.sent }}/{{ campaignJob.job.total }}
          </span>
          <span
            v-if="isCampaignQuotaWaiting(campaignJob.job)"
            class="campaign-wait-text"
            :title="campaignJob.job.nextEligibleAt || ''"
          >
            {{ formatCampaignNextEligible(campaignJob.job.nextEligibleAt) }}
          </span>
          <div class="campaign-controls" v-if="campaignJob.job && (campaignJob.job.status === 'pending' || campaignJob.job.status === 'processing')">
            <button
              type="button"
              class="campaign-control-btn stop"
              :disabled="campaignActionBusy"
              @click.stop.prevent="stopCampaignJob"
            >
              {{ campaignActionBusy ? $t('...') : $t('Stop') }}
            </button>
            <button
              type="button"
              class="campaign-control-btn relaunch"
              :disabled="campaignActionBusy"
              @click.stop.prevent="relaunchCampaignJob"
            >
              {{ $t('Relaunch') }}
            </button>
          </div>
        </div>
      </li>
      
      <!-- Language Dropdown -->
      <base-dropdown
        tag="li"
        :menu-on-right="!$rtl.isRTL"
        title-tag="a"
        title-classes="nav-link"
        class="nav-item"
        menu-classes="dropdown-language-grid"
      >
        <template slot="title">
          <i class="tim-icons icon-world"></i>
          <span class="d-lg-none">{{ $t('Language') }}</span>
        </template>
        <li class="nav-link" v-for="lang in availableLanguages" :key="lang.code">
          <a href="#" class="nav-item dropdown-item" @click.prevent="changeLanguage(lang.code)">
            <span :class="{'font-weight-bold': $i18n.locale === lang.code}">{{ lang.name }}</span>
          </a>
        </li>
      </base-dropdown>
      
      <!-- Action Items / Notifications Dropdown -->
      <base-dropdown
        tag="li"
        :menu-on-right="!$rtl.isRTL"
        title-tag="a"
        title-classes="nav-link"
        class="nav-item"
      >
        <template slot="title">
          <i class="tim-icons icon-bell-55" :class="{'text-danger': actionItemsCount > 0}"></i>
          <p class="d-lg-none">{{ $t('Action Items') }}</p>
          <div class="notification-badge" v-if="actionItemsCount > 0">{{ actionItemsCount > 99 ? '99+' : actionItemsCount }}</div>
        </template>
        <li class="nav-link" v-if="actionItems.length === 0">
          <span class="nav-item dropdown-item text-success">
            <i class="tim-icons icon-check-2 mr-2"></i>{{ $t('All caught up!') }}
          </span>
        </li>
        <li class="nav-link" v-for="(item, index) in actionItems" :key="index">
          <router-link 
            :to="item.link" 
            class="nav-item dropdown-item d-flex align-items-center"
            @click.native="onActionItemClick"
          >
            <span class="action-icon mr-2" :class="'text-' + item.color">
              <i :class="'tim-icons ' + item.icon"></i>
            </span>
            <span class="flex-grow-1">{{ item.text }}</span>
            <span class="badge ml-2" :class="'badge-' + item.color">{{ item.count }}</span>
          </router-link>
        </li>
        <div class="dropdown-divider"></div>
        <li class="nav-link">
          <a href="#" class="nav-item dropdown-item text-center" @click.prevent="fetchActionItems">
            <i class="tim-icons icon-refresh-02"></i> {{ $t('Refresh') }}
          </a>
        </li>
      </base-dropdown>
      
      <!-- User Menu Dropdown -->
      <base-dropdown
        tag="li"
        :menu-on-right="!$rtl.isRTL"
        title-tag="a"
        class="nav-item"
        title-classes="nav-link"
        menu-classes="dropdown-navbar user-menu-dropdown"
      >
        <template slot="title">
          <div class="photo">
            <img v-if="userImage" :src="userImage" alt="User" />
            <i v-else class="tim-icons icon-single-02" style="font-size: 20px; line-height: 34px;"></i>
          </div>
          <b class="caret d-none d-lg-block d-xl-block"></b>
          <p class="d-lg-none">{{ userName }}</p>
        </template>
        
        <!-- User Info -->
        <li class="nav-link">
          <span class="nav-item dropdown-item disabled">
            <i class="tim-icons icon-single-02 mr-2"></i>{{ userName }}
          </span>
        </li>
        <div class="dropdown-divider"></div>
        
        <!-- Profile Link -->
        <li class="nav-link">
          <router-link to="/profile" class="nav-item dropdown-item">
            <i class="tim-icons icon-badge mr-2"></i>{{ $t('Profile') }}
          </router-link>
        </li>
        <div class="dropdown-divider"></div>
        
        <!-- Appearance Settings -->
        <li class="nav-link">
          <span class="nav-item dropdown-item dropdown-header">{{ $t('Appearance') }}</span>
        </li>
        
        <!-- Sidebar Color -->
        <li class="nav-link">
          <div class="nav-item dropdown-item sidebar-colors">
            <span class="mr-2">{{ $t('Color') }}:</span>
            <span
              v-for="item in sidebarColors"
              :key="item.color"
              class="badge filter"
              :class="[`badge-${item.color}`, { active: item.active }]"
              @click="changeSidebarBackground(item)"
            ></span>
          </div>
        </li>
        
        <!-- Dark/Light Mode -->
        <li class="nav-link">
          <div class="nav-item dropdown-item d-flex align-items-center">
            <span class="mr-2">{{ $t('Dark Mode') }}:</span>
            <base-switch v-model="darkMode" @input="toggleMode" on-text="ON" off-text="OFF"></base-switch>
          </div>
        </li>
        
        <!-- Sidebar Mini -->
        <li class="nav-link">
          <div class="nav-item dropdown-item d-flex align-items-center">
            <span class="mr-2">{{ $t('Sidebar Mini') }}:</span>
            <base-switch v-model="sidebarMini" @input="minimizeSidebar" on-text="ON" off-text="OFF"></base-switch>
          </div>
        </li>
        
        <div class="dropdown-divider"></div>
        
        <!-- Logout -->
        <li class="nav-link">
          <router-link to="/logout" class="nav-item dropdown-item text-danger">
            <i class="tim-icons icon-button-power mr-2"></i>{{ $t('Logout') }}
          </router-link>
        </li>
      </base-dropdown>
    </ul>
  </base-nav>
</template>

<script>
import { BaseNav, Modal, BaseSwitch } from '@/components';
import SidebarToggleButton from './SidebarToggleButton';
import { mapGetters, mapMutations } from 'vuex';
import swal from 'sweetalert2';

const swalDark = { background: '#1e1e2f', color: '#ffffff' };

export default {
  components: {
    SidebarToggleButton,
    BaseNav,
    Modal,
    BaseSwitch
  },
  computed: {
    ...mapGetters(['getResources']),
    routeName() {
      const { name } = this.$route;
      return this.capitalizeFirstLetter(name);
    },
    isRTL() {
      return this.$rtl.isRTL;
    },
    userName() {
      if (this.user && this.user.firstName) {
        return this.user.firstName + (this.user.lastName ? ' ' + this.user.lastName : '');
      }
      return (this.user && this.user.username) ? this.user.username : 'User';
    },
    userImage() {
      if (this.user && this.user.image) {
        return 'users/' + this.user.image;
      }
      return null;
    },
    unreadNotifications() {
      return this.actionItemsCount;
    },
    actionItemsCount() {
      return this.actionItems.reduce((sum, item) => sum + item.count, 0);
    },
    availableLanguages() {
        const names = {
            en: 'English',
            nl: 'Nederlands',
            fr: 'Français',
            es: 'Español',
            de: 'Deutsch',
            it: 'Italiano',
            pt: 'Português',
            ru: 'Русский',
            zh: '中文',
            ja: '日本語',
            ar: 'العربية',
            ko: '한국어',
            th: 'ไทย',
            vi: 'Tiếng Việt',
            el: 'Ελληνικά',
            pl: 'Polski',
            ro: 'Română',
            tr: 'Türkçe',
            no: 'Norsk',
            fi: 'Suomi',
            sv: 'Svenska',
            da: 'Dansk',
            hi: 'हिन्दी',
            ur: 'اردو',
            uk: 'Українська',
            be: 'Беларуская',
            et: 'Eesti',
            lv: 'Latviešu',
            lt: 'Lietuvių',
            fa: 'فارسی',
            id: 'Indonesia',
            ms: 'Melayu (Malaysia)'
        };
        // Fallback for older i18n versions if availableLocales is missing
        const locales = this.$i18n.availableLocales || Object.keys(this.$i18n.messages || {});
        
        return locales.map(code => ({
            code,
            name: names[code] || code.toUpperCase()
        }));
    },
    isAdmin() {
      // Check if user has admin permissions (manageSystem)
      const resources = (this.getResources && Array.isArray(this.getResources.resources))
        ? this.getResources.resources
        : [];
      return resources.some(r => r.rkey === 'manageSystem');
    }
  },
  watch: {
    // Close mobile menu when route changes
    $route() {
      this.showMenu = false;
    }
  },
  data() {
    return {
      user: {},
      activeNotifications: false,
      showMenu: false,
      searchModalVisible: false,
      searchQuery: '',
      notifications: [],
      actionItems: [],
      darkMode: true,
      sidebarMini: true,
      campaignJob: null,
      campaignPollInterval: null,
      campaignHeartbeatInterval: null,
      actionItemsInterval: null,
      workerTriggered: false,
      campaignActionBusy: false,
      sidebarColors: [
        { color: 'primary', active: false, value: 'primary' },
        { color: 'vue', active: true, value: 'vue' },
        { color: 'info', active: false, value: 'blue' },
        { color: 'success', active: false, value: 'green' },
        { color: 'warning', active: false, value: 'orange' },
        { color: 'danger', active: false, value: 'red' }
      ]
    };
  },
  methods: {
    ...mapMutations([
      'setToSearch'
    ]),
    capitalizeFirstLetter(string) {
      return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
    },
    toggleNotificationDropDown() {
      this.activeNotifications = !this.activeNotifications;
    },
    closeDropDown() {
      this.activeNotifications = false;
    },
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    hideSidebar() {
      this.$sidebar.displaySidebar(false);
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    performSearch() {
      if (this.searchQuery.trim()) {
        this.setToSearch(this.searchQuery);
        this.searchModalVisible = false;
        this.$router.push('/system/search');
      }
    },
    fetchNotifications() {
      this.$http.get('notifications')
        .then(response => response.json())
        .then(data => {
          this.notifications = data || [];
        })
        .catch(error => {
          if (error.status === 403) {
            this.$router.push('/logout');
          }
        });
    },
    fetchActionItems() {
      this.$http.get('actionitems')
        .then(response => response.json())
        .then(data => {
          this.actionItems = data.items || [];
        })
        .catch(error => {
          console.error('Error fetching action items:', error);
        });
    },
    refreshNotifications() {
      this.fetchNotifications();
      this.fetchActionItems();
    },
    onActionItemClick() {
      // Refresh action items after navigation to update badge count
      // Use timeout to allow the page to load and process the action
      setTimeout(() => {
        this.fetchActionItems();
      }, 2000);
    },
    fetchUserProfile() {
      this.$http.get('profile')
        .then(response => response.json())
        .then(data => {
          this.user = data;
          if(this.user.language) {
              this.$i18n.locale = this.user.language;
              localStorage.setItem('lang', this.user.language);
              // Update RTL if Arabic
              // if (this.user.language === 'ar') {
              //   this.$rtl.enableRTL();
              // } else {
                this.$rtl.disableRTL();
              // }
          }
          if(this.user.dark_mode !== null && this.user.dark_mode !== undefined) {
              this.darkMode = this.user.dark_mode == 1;
              this.toggleMode(this.darkMode, false); // false = don't save again
          }
        })
        .catch(error => {
          console.error('Error fetching profile:', error);
        });
    },
    changeLanguage(langCode) {
      this.$i18n.locale = langCode;
      localStorage.setItem('lang', langCode);
      // Update RTL if Arabic
      // if (langCode === 'ar') {
      //   this.$rtl.enableRTL();
      // } else {
        this.$rtl.disableRTL();
      // }
      this.$http.put('profile', { language: langCode });
    },
    changeSidebarBackground(item) {
      this.sidebarColors.forEach(c => c.active = false);
      item.active = true;
      this.$emit('sidebar-color-change', item.value);
      // Dispatch event for DashboardLayout
      this.$root.$emit('change-sidebar-color', item.value);
    },
    toggleMode(isDark, save = true) {
      let docClasses = document.body.classList;
      if (isDark) {
        docClasses.remove('white-content');
      } else {
        docClasses.add('white-content');
      }
      localStorage.setItem('darkMode', isDark ? '1' : '0');
      if(save) {
          this.$http.put('profile', { dark_mode: isDark ? 1 : 0 });
      }
    },
    minimizeSidebar() {
      this.$sidebar.toggleMinimize();
    },
    loadSavedSettings() {
      // Load dark mode preference from local storage as fallback
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode !== null && !this.user.dark_mode) {
        this.darkMode = savedDarkMode === '1';
        this.toggleMode(this.darkMode, false);
      }
    },
    fetchCampaignStatus() {
      this.$http.get('campaignstatus')
        .then(response => response.json())
        .then(data => {
          this.campaignJob = data;
          
          // If job is pending and needs worker, trigger the worker
          if (data.needsWorker && !this.workerTriggered) {
            this.workerTriggered = true;
            this.$http.get('campaignworker')
              .then(() => {
                this.workerTriggered = false;
              })
              .catch(() => {
                this.workerTriggered = false;
              });
          }
          
          // If active, poll more frequently
          if (data.active && !this.campaignPollInterval) {
            this.campaignPollInterval = setInterval(() => {
              this.fetchCampaignStatus();
            }, 2000); // Poll every 2 seconds during active job
          } else if (!data.active && this.campaignPollInterval) {
            clearInterval(this.campaignPollInterval);
            this.campaignPollInterval = null;
            // Show completion notification if just completed
            if (data.completed) {
              this.$notify({
                message: `Campaign complete! Sent ${data.job.sent}/${data.job.total} emails.`,
                icon: 'tim-icons icon-check-2',
                horizontalAlign: 'right',
                verticalAlign: 'top',
                type: 'success',
                timeout: 5000
              });
            }
          }
        })
        .catch(() => {
          // Silently fail for polling
        });
    },
    parseLocalDateTime(value) {
      const str = String(value || '').trim();
      if (!str) return null;
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (m) {
        const [, y, mo, d, h, mi, s] = m;
        const dt = new Date(
          Number(y),
          Number(mo) - 1,
          Number(d),
          Number(h),
          Number(mi),
          Number(s || 0)
        );
        return Number.isNaN(dt.getTime()) ? null : dt;
      }
      const fallback = new Date(str);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    },
    isCampaignQuotaWaiting(job) {
      if (!job || job.status !== 'pending' || !job.nextEligibleAt) return false;
      const dt = this.parseLocalDateTime(job.nextEligibleAt);
      if (!dt) return true;
      return dt.getTime() > Date.now();
    },
    formatCampaignNextEligible(value) {
      const dt = this.parseLocalDateTime(value);
      if (!dt) return 'Waiting for quota';
      return `Waiting until ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    },
    campaignJobAction(action) {
      if (this.campaignActionBusy) return;
      if (!this.campaignJob || !this.campaignJob.job || !this.campaignJob.job.id) return;

      const isStop = action === 'stop';
      swal.fire({
        title: this.$t('areYouSure'),
        text: isStop
          ? this.$t('Stop current campaign sending?')
          : this.$t('Relaunch current campaign from start?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: isStop ? this.$t('Stop') : this.$t('Relaunch'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: isStop ? 'btn btn-danger btn-fill' : 'btn btn-info btn-fill',
          cancelButton: 'btn btn-default btn-fill'
        },
        buttonsStyling: false,
        ...(document.body.classList.contains('white-content') ? {} : swalDark)
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.campaignActionBusy = true;
        const payload = {
          action,
          jobId: this.campaignJob.job.id
        };

        this.$http.put('campaignstatus', payload)
          .then(response => response.json())
          .then(data => {
            if (!data || data.success !== true) {
              throw new Error((data && data.error) ? data.error : this.$t('Failed to update campaign'));
            }

            if (data.statusPayload) {
              this.campaignJob = data.statusPayload;
            }

            if (action === 'relaunch') {
              this.workerTriggered = false;
              this.$http.get('campaignworker').catch(() => {});
            }

            this.$notify({
              message: data.message || (isStop ? this.$t('Campaign stopped') : this.$t('Campaign relaunched')),
              icon: isStop ? 'tim-icons icon-simple-remove' : 'tim-icons icon-refresh-02',
              horizontalAlign: 'right',
              verticalAlign: 'top',
              type: isStop ? 'warning' : 'info',
              timeout: 4000
            });

            this.fetchCampaignStatus();
          })
          .catch((error) => {
            this.$notify({
              message: error.message || this.$t('Failed to update campaign'),
              icon: 'tim-icons icon-alert-circle-exc',
              horizontalAlign: 'right',
              verticalAlign: 'top',
              type: 'danger',
              timeout: 5000
            });
          })
          .finally(() => {
            this.campaignActionBusy = false;
          });
      });
    },
    stopCampaignJob() {
      this.campaignJobAction('stop');
    },
    relaunchCampaignJob() {
      this.campaignJobAction('relaunch');
    }
  },
  mounted() {
    this.fetchNotifications();
    this.fetchActionItems();
    this.fetchUserProfile();
    this.loadSavedSettings();
    this.fetchCampaignStatus();
    
    // Refresh action items every 60 seconds
    this.actionItemsInterval = setInterval(() => {
      this.fetchActionItems();
    }, 60000);
    
    // Check campaign status every 10 seconds (initial slower poll)
    this.campaignHeartbeatInterval = setInterval(() => {
      if (!this.campaignPollInterval) {
        this.fetchCampaignStatus();
      }
    }, 10000);
  },
  beforeDestroy() {
    if (this.actionItemsInterval) {
      clearInterval(this.actionItemsInterval);
    }
    if (this.campaignPollInterval) {
      clearInterval(this.campaignPollInterval);
    }
    if (this.campaignHeartbeatInterval) {
      clearInterval(this.campaignHeartbeatInterval);
    }
  }
};
</script>

<style scoped>
.top-navbar {
  top: 0px;
}

.photo {
  display: inline-block;
  height: 34px;
  width: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  text-align: center;
  overflow: hidden;
}

.photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo i {
  color: rgba(255, 255, 255, 0.8);
}

.white-content .photo {
  background: rgba(0, 0, 0, 0.1);
}

.white-content .photo i {
  color: #344675;
}

.notification {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 10px;
  height: 10px;
  background: #f5365c;
  border-radius: 50%;
}

.notification-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #f5365c;
  border-radius: 9px;
  font-size: 10px;
  font-weight: bold;
  color: #fff;
  text-align: center;
  line-height: 18px;
}

.action-icon {
  width: 24px;
  text-align: center;
}

.action-icon i {
  font-size: 16px;
}

.dropdown-item.disabled {
  color: #9a9a9a;
  pointer-events: none;
}

.dropdown-item i {
  width: 20px;
}

.dropdown-header {
  font-size: 11px;
  text-transform: uppercase;
  color: #9a9a9a;
  pointer-events: none;
}

.sidebar-colors .badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  display: inline-block;
  margin-right: 3px;
  border: 2px solid transparent;
}

.sidebar-colors .badge.active {
  border-color: #fff;
}

.badge-vue {
  background-color: #42b883;
}

.user-menu-dropdown {
  min-width: 250px;
}

/* Fix for Search Modal in Dark Mode */
::v-deep .modal-search .modal-content {
    background-color: #1e1e2f;
    color: #ffffff;
}

::v-deep .modal-search .form-control {
    color: #ffffff;
}

::v-deep .modal-search .close {
    color: #ffffff;
}

/* Fix for Dropdowns in Dark Mode (Search, Notification, Language) - Desktop only */
@media (min-width: 992px) {
  ::v-deep .dropdown-menu {
      background-color: #1e1e2f !important;
  }
}

::v-deep .dropdown-item {
    color: rgba(255, 255, 255, 0.7) !important;
}

::v-deep .dropdown-item:hover, 
::v-deep .dropdown-item:focus {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #ffffff !important;
}

/* Light Mode Overrides - Desktop */
@media (min-width: 992px) {
  .white-content ::v-deep .dropdown-menu {
      background-color: #ffffff !important;
  }
}

.white-content ::v-deep .dropdown-item {
    color: #344675 !important;
}

.white-content ::v-deep .dropdown-item:hover, 
.white-content ::v-deep .dropdown-item:focus {
    background-color: #e4e4e4 !important;
    color: #344675 !important;
}

.white-content ::v-deep .modal-search .modal-content {
    background-color: #ffffff;
    color: #344675;
}

.white-content ::v-deep .modal-search .form-control {
    color: #344675;
}

/* Campaign Progress Indicator */
.campaign-progress-item {
  margin-right: 15px;
}

.campaign-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(145deg, rgba(66, 184, 131, 0.15), rgba(66, 184, 131, 0.25));
  border: 1px solid rgba(66, 184, 131, 0.4);
  padding: 6px 14px;
  border-radius: 20px;
  color: #42b883;
  font-weight: 600;
  font-size: 13px;
}

.campaign-progress i {
  font-size: 16px;
}

.sending-animation {
  animation: pulse-send 1.5s ease-in-out infinite;
}

@keyframes pulse-send {
  0%, 100% {
    opacity: 1;
    transform: translateX(0);
  }
  50% {
    opacity: 0.6;
    transform: translateX(3px);
  }
}

.progress-text {
  font-family: 'Monaco', 'Menlo', monospace;
  letter-spacing: 0.5px;
}

.campaign-controls {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
}

.campaign-wait-text {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px dashed rgba(255, 193, 7, 0.35);
  background: rgba(255, 193, 7, 0.08);
  color: #ffcf5c;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.1;
  white-space: nowrap;
}

.campaign-control-btn {
  border: 1px solid rgba(66, 184, 131, 0.45);
  background: rgba(66, 184, 131, 0.12);
  color: inherit;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
  padding: 5px 9px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.campaign-control-btn:hover:not(:disabled) {
  background: rgba(66, 184, 131, 0.22);
}

.campaign-control-btn.stop {
  border-color: rgba(245, 54, 92, 0.5);
  color: #ff7f9a;
  background: rgba(245, 54, 92, 0.12);
}

.campaign-control-btn.stop:hover:not(:disabled) {
  background: rgba(245, 54, 92, 0.2);
}

.campaign-control-btn.relaunch {
  border-color: rgba(50, 134, 255, 0.45);
  color: #66b3ff;
  background: rgba(50, 134, 255, 0.12);
}

.campaign-control-btn.relaunch:hover:not(:disabled) {
  background: rgba(50, 134, 255, 0.22);
}

.campaign-control-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.white-content .campaign-progress {
  background: linear-gradient(145deg, rgba(66, 184, 131, 0.1), rgba(66, 184, 131, 0.2));
  color: #2d8a5e;
}

.white-content .campaign-wait-text {
  border-color: rgba(176, 125, 0, 0.35);
  background: rgba(255, 193, 7, 0.12);
  color: #946200;
}

.white-content .campaign-control-btn {
  border-color: rgba(45, 138, 94, 0.35);
  background: rgba(45, 138, 94, 0.12);
}

.white-content .campaign-control-btn.stop {
  border-color: rgba(245, 54, 92, 0.35);
  color: #c23153;
  background: rgba(245, 54, 92, 0.1);
}

.white-content .campaign-control-btn.relaunch {
  border-color: rgba(0, 102, 204, 0.35);
  color: #0066cc;
  background: rgba(0, 102, 204, 0.1);
}

/* Language Dropdown Grid - Desktop Only */
::v-deep .dropdown-language-grid {
  min-width: 450px !important;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-height: 480px;
  overflow-y: auto;
  padding: 10px;
}

::v-deep .dropdown-language-grid .nav-link {
  padding: 5px;
}

::v-deep .dropdown-language-grid .dropdown-item {
  padding: 10px 15px;
  border-radius: 4px;
}

/* ===========================================
   MOBILE LANGUAGE DROPDOWN - 3-Column Grid
   (Colors handled by global _navbar.scss and _white-content.scss)
   =========================================== */
@media (max-width: 991px) {
  /* Grid layout for language dropdown */
  ::v-deep .dropdown-language-grid {
    min-width: auto !important;
    width: 100% !important;
    display: none !important;
    max-height: 220px;
    overflow-y: auto;
    padding: 8px !important;
    margin: 0 !important;
    border-radius: 8px !important;
  }

  ::v-deep .dropdown.show .dropdown-language-grid {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    animation: fadeInDropdown 0.25s ease-out;
  }

  ::v-deep .dropdown-language-grid .nav-link {
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .dropdown-language-grid .dropdown-item {
    padding: 8px 4px !important;
    border-radius: 6px !important;
    font-size: 11px !important;
    text-align: center !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Animation for all dropdown menus */
  ::v-deep .dropdown.show .dropdown-menu {
    animation: fadeInDropdown 0.25s ease-out;
  }

  /* Spacing: Add margin between header bar and dropdown menu */
  ::v-deep .navbar-collapse {
    margin-top: 15px !important;
  }

  /* Notification badge - inline bubble after text */
  .notification-badge {
    position: static !important;
    display: inline-block;
    vertical-align: middle;
    margin-left: 10px;
  }

  /* Menu items - left aligned */
  ::v-deep .navbar-nav {
    align-items: flex-start !important;
  }

  ::v-deep .navbar-nav .nav-item {
    width: 100%;
  }

  ::v-deep .navbar-nav .nav-link {
    display: flex !important;
    align-items: center;
    justify-content: flex-start !important;
    text-align: left !important;
  }

  /* Spacing between icon and text in mobile nav items */
  ::v-deep .navbar-nav .nav-link i {
    margin-right: 10px;
  }

  ::v-deep .navbar-nav .nav-link p,
  ::v-deep .navbar-nav .nav-link .d-lg-none {
    margin-left: 5px;
    margin-bottom: 0;
  }
}

/* FadeIn animation for dropdown menus */
@keyframes fadeInDropdown {
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
